


import { Component, Prop, Vue } from "vue-property-decorator";

import { IsPackage, IsTiered, IsUnitBased } from "Shared/Models";

import { IDecimal } from "App/Contracts";
import {
    Product,
    Plan,
    Price,
    GlobalConfiguration
} from "App/Models";
import { Plans } from "App";
import { Decimal, DeepCopy, EventBus, $t } from "App/IOC";

import { InjectReactive } from "Web/Libs/VuePropertyDecorators";
import PlanForm from "Web/Views/Products/Single/Plans/Form.vue";
import PriceTargetForm from "Web/Views/Products/Single/Prices/PriceTarget.vue";
import PriceHistory from "Web/Views/Products/Single/Prices/History.vue";
import ProductPrices from "Web/Views/Products/Single/Prices/Index.vue";

/* Stripe rejects a product name longer than this; the billing code truncates as a last resort, but
   we surface the count here so a plan can be fixed before it ever overflows. */
const STRIPE_PRODUCT_NAME_MAX = 250;

@Component({
    components: {
        PriceTargetForm,
        PriceHistory,
        ProductPrices,
        PlanForm
    }
})
export default class ProductPlan extends Vue {
    @Prop() readonly Product!: Product;
    @Prop() readonly Record!: Plan;

    @Prop( { default: false } )
    readonly ReadOnly!: boolean;

    /* True when this plan still has Active subscriptions on an outdated price snapshot: a price was
       published but "apply the new prices to customers" has not yet been run for it. */
    @Prop( { default: false } )
    readonly Outdated!: boolean;

    @InjectReactive( { from: "configuration", default: undefined } )
    private configuration?: GlobalConfiguration;

    private showForm: boolean = false;
    private showPlanForm: boolean = false;
    private showHistory: boolean = false;

    /* Draft state reported by each child price (keyed by price Id), drives the header flag. */
    private draftPrices: { [ id: number ]: boolean } = {};

    private onDraftState( payload: { Id: number; HasDraft: boolean } ): void {
        this.$set( this.draftPrices, payload.Id, payload.HasDraft );
    }

    private get hasDraftPrice(): boolean {
        return Object.keys( this.draftPrices ).some(
            ( id: string ): boolean => this.draftPrices[ Number( id ) ]
        );
    }

    /* Preview of the product name that billing sends to Stripe for this plan, mirroring the backend
       builder (Subscriptions usecase: getInvoiceLineDescription + getDefaultPriceQuantity →
       `${planName} (${items})`). Only the plan's own included prices land in the parenthetical
       (product-global prices bill as separate Stripe lines), so Record.Prices is the full input.
       Kept in sync with that backend builder. */
    private get stripeProductName(): string {
        const planName = this.Record.Name !== undefined ? this.Record.Name : "";

        const items = this.Record.Prices
            .filter( ( price: Price ): boolean => price.Included )
            .map( ( price: Price ): string => this.priceDescription( price ) )
            .filter( ( label: string ): boolean => label !== "" );

        return `${planName} (${items.join( ", " )})`;
    }

    private get stripeNameLength(): number {
        return this.stripeProductName.length;
    }

    private get stripeNameMax(): number {
        return STRIPE_PRODUCT_NAME_MAX;
    }

    private get stripeNameOverLimit(): boolean {
        return this.stripeNameLength > STRIPE_PRODUCT_NAME_MAX;
    }

    /* One descriptif item for a price: a package shows its name, an unlimited quantity reads
       "<plural> illimité(e)s", everything else "<quantity> <unit|plural>". Prefers the price
       target's labels (the editable unit names) and falls back to the price's own. */
    private priceDescription( price: Price ): string {
        const target = price.Target;

        const name = target !== undefined && target.PriceName !== undefined
            ? target.PriceName
            : ( price.Name !== undefined ? price.Name : "" );

        if( IsPackage( price ) )
            return name;

        const unit = target !== undefined && target.PriceUnitName !== undefined
            ? target.PriceUnitName
            : ( price.UnitName !== undefined ? price.UnitName : "" );

        const plural = target !== undefined && target.PricePluralUnitName !== undefined
            ? target.PricePluralUnitName
            : ( price.PluralUnitName !== undefined ? price.PluralUnitName : "" );

        const quantity = this.defaultQuantity( price );

        if( this.configuration !== undefined &&
            quantity.IsEqual( this.configuration.UnlimitedQuantity as IDecimal )
        )
            return $t( "GLOBAL_unlimited", plural );

        return `${quantity} ${quantity.GreaterThan( 1 ) ? plural : unit}`;
    }

    /* Default quantity a fresh subscription line gets for this price, mirroring the backend
       getDefaultPriceQuantity (tiered → first tier max, unit → min, otherwise included → 1). */
    private defaultQuantity( price: Price ): IDecimal {
        if( IsTiered( price ) && price.Tiers.length > 0 )
            return price.Tiers[ 0 ].MaxQuantity;

        if( IsUnitBased( price ) && price.MinQuantity !== undefined )
            return price.MinQuantity;

        if( price.Included )
            return new Decimal( 1 );

        return new Decimal( 0 );
    }

    private async duplicate(): Promise<void> {
        if( this.ReadOnly )
            return;

        this.Record.Product = DeepCopy( this.Product );

        const [ duplicatedPlan, error ] = await Plans.Duplicate( this.Record );

        if( error !== undefined || duplicatedPlan === undefined )
            EventBus.Publish( "Error", error );

        this.$emit( "Refresh" );
    }

    private async archivePlan(): Promise<void> {
        if( this.ReadOnly )
            return;

        EventBus.Publish( "Confirmation", {
            Props: {
                Message: this.$tc( "PLAN_archiveConfirm", 1 ),
                Actions: [{
                    Id: "archive",
                    Name: this.$t( "GLOBAL_actionArchive" ),
                    Active: true
                }]
            },

            Events: { Action: this.archive }
        });
    }

    private async archive(): Promise<void> {
        const error = await Plans.Archive( this.Record );

        if( error !== undefined )
            EventBus.Publish( "Error", error );

        this.$emit( "Refresh" );
    }

    private async update( plan: Plan ): Promise<void> {
        if( this.ReadOnly )
            return;

        const [ , error ] = await Plans.Update( this.Record as Plan, plan );

        if( error !== undefined )
            EventBus.Publish( "Error", error );

        this.$emit( "Refresh" );
    }

    private createPrice( price: Price ): void {
        this.$emit( "CreatePrice", price, this.Record );
    }

    private setDefault(): void {
        if( this.Record.IsDefault )
            return;

        this.$emit( "Default" );
    }
}
